import React from 'react';
import { ArrowRight, Play, ExternalLink, Calendar, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import Header from '../components/Header';
import Hero from '../components/Hero';
import About from '../components/About';
import Ventures from '../components/Ventures';
import Achievements from '../components/Achievements';
import Education from '../components/Education';
import Clients from '../components/Clients';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { usePortfolio } from '../context/PortfolioContext';

// Limited Speaking Section Component
const LimitedTalksSection = () => {
  const navigate = useNavigate();
  const { talks } = usePortfolio();
  const [ref, isVisible] = useScrollAnimation(0.1);
  const limitedTalks = (talks || []).filter(talk => talk.featured).slice(0, 3);

  if (!limitedTalks.length) return null;

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="limited-talks-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-12">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Speaking Engagements
            </h2>
            <p className="text-lg text-gray-700">
              Talks, panels, and keynotes on technology and entrepreneurship.
            </p>
          </div>
        </div>

        <div ref={ref} className="grid md:grid-cols-3 gap-8">
          {limitedTalks.map((talk, index) => (
            <div
              key={talk.id}
              className={`${isVisible ? 'animate-fade-in-up' : 'animate-on-scroll'}`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <Card className="border-gray-200 hover:shadow-2xl transition-all duration-500 overflow-hidden group h-full transform hover:-translate-y-2">
                <div className="relative aspect-video bg-gray-200 overflow-hidden">
                  <img
                    src={talk.thumbnail}
                    alt={talk.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play className="text-white" size={48} />
                  </div>
                  {talk.featured && (
                    <Badge className="absolute top-4 left-4 bg-gray-900 text-white">
                      Featured
                    </Badge>
                  )}
                </div>
                <CardContent className="p-6">
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                      {talk.title}
                    </h3>
                    <p className="text-sm text-gray-700 font-medium">{talk.event}</p>
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <Calendar size={14} />
                      {talk.date}
                    </p>
                    {talk.videoUrl && (
                      <a
                        href={talk.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors"
                      >
                        Watch Talk
                        <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <Button
            onClick={() => navigate('/insights')}
            size="lg"
            variant="outline"
            className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white group"
            data-testid="talks-read-more-btn"
          >
            View All Speaking Engagements
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
          </Button>
        </div>
      </div>
    </section>
  );
};

// Limited Articles Section Component
const LimitedArticlesSection = () => {
  const navigate = useNavigate();
  const { media } = usePortfolio();
  const [ref, isVisible] = useScrollAnimation(0.1);
  const limitedArticles = (media || []).filter(article => article.featured).slice(0, 3);

  if (!limitedArticles.length) return null;

  return (
    <section className="py-24 md:py-32 bg-gray-50" data-testid="limited-articles-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-12">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Media Coverage
            </h2>
            <p className="text-lg text-gray-700">
              Featured interviews, articles, and news coverage.
            </p>
          </div>
        </div>

        <div ref={ref} className="grid md:grid-cols-3 gap-8">
          {limitedArticles.map((article, index) => (
            <div
              key={article.id}
              className={`${isVisible ? 'animate-fade-in-up' : 'animate-on-scroll'}`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <Card className="border-gray-200 hover:shadow-2xl transition-all duration-500 overflow-hidden group h-full transform hover:-translate-y-2">
                <div className="relative aspect-video bg-gray-200 overflow-hidden">
                  <img
                    src={article.thumbnail}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {article.featured && (
                    <Badge className="absolute top-4 left-4 bg-gray-900 text-white">
                      Featured
                    </Badge>
                  )}
                </div>
                <CardContent className="p-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FileText size={14} />
                      {article.publication}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed line-clamp-2">
                      {article.description}
                    </p>
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors"
                    >
                      Read Article
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <Button
            onClick={() => navigate('/insights')}
            size="lg"
            variant="outline"
            className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white group"
            data-testid="articles-read-more-btn"
          >
            View All Articles
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
          </Button>
        </div>
      </div>
    </section>
  );
};

const AboutPage = () => {
  return (
    <div data-testid="about-page">
      <SEO 
        title="About Mir Shahrukh Islam | Portfolio & Ventures"
        description="14+ years of experience building 3 technology companies serving 600+ enterprise clients. Forbes 30 Under 30 Asia, Stanford Seed alumnus."
        url="/about"
      />
      <Header />
      <Hero />
      <About />
      <Ventures />
      <Achievements />
      <Education />
      <Clients />
      <LimitedTalksSection />
      <LimitedArticlesSection />
      <Footer />
    </div>
  );
};

export default AboutPage;